import React, { useReducer, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from "recharts";
import ThreatDetailModal from "./ThreatDetailModal";
import { Threat, Likelihood, DashboardRouteState } from "../types";
import { dashboardReducer, dashboardInitialState } from "../store/reducer";
import { ThreatSourcesResponse, generateAllReport } from "../services/threat_model_services";

const C = {
  purple: "#8a2be2",
  green:  "#28c76f",
  red:    "#ff4d4f",
  blue:   "#00aaff",
  yellow: "#ffc107",
};

const LIKELIHOOD_COLORS = [C.red, C.blue, C.green];
const SOURCE_COLORS     = [C.purple, C.blue, C.green, C.yellow];

// ── Data derivation ───────────────────────────────────────────────────────────

interface ChartData {
  likelihoodData: { name: string; value: number }[];
  strideData:     { name: string; High: number; Med: number; Low: number }[];
  targetData:     { name: string; High: number; Med: number; Low: number }[];
  sourcesData:    { name: string; value: number }[];
}

function deriveChartData(threats: Threat[]): ChartData {
  const lCounts: Record<string, number> = { High: 0, Med: 0, Low: 0 };
  const strideMap: Record<string, { High: number; Med: number; Low: number }> = {};
  const targetMap: Record<string, { High: number; Med: number; Low: number }> = {};
  const sourceMap: Record<string, number> = {};

  for (const t of threats) {
    const lik = t.likelihood as "High" | "Med" | "Low";
    lCounts[lik] = (lCounts[lik] ?? 0) + 1;

    if (t.stride) {
      if (!strideMap[t.stride]) strideMap[t.stride] = { High: 0, Med: 0, Low: 0 };
      strideMap[t.stride][lik] = (strideMap[t.stride][lik] ?? 0) + 1;
    }

    if (t.target) {
      if (!targetMap[t.target]) targetMap[t.target] = { High: 0, Med: 0, Low: 0 };
      targetMap[t.target][lik] = (targetMap[t.target][lik] ?? 0) + 1;
    }

    if (t.source) {
      sourceMap[t.source] = (sourceMap[t.source] ?? 0) + 1;
    }
  }

  return {
    likelihoodData: Object.entries(lCounts)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({ name, value })),
    strideData: Object.entries(strideMap).map(([name, counts]) => ({ name, ...counts })),
    targetData: Object.entries(targetMap).map(([name, counts]) => ({ name, ...counts })),
    sourcesData: Object.entries(sourceMap).map(([name, value]) => ({ name, value })),
  };
}

function normalizeSeverity(severity?: string): Likelihood {
  switch (severity?.toLowerCase()) {
    case "critical":
    case "high":   return "High";
    case "medium": return "Med";
    default:       return "Low";
  }
}

function mapThreatSourcesToThreats(data: ThreatSourcesResponse): Threat[] {
  const ts = data.results?.threat_sources;
  if (!ts) return [];

  const vectors = ts.attack_vectors ?? [];
  const actors  = ts.threat_actors  ?? [];

  const externalLabel = actors.some((a) => a.actor_type === "external") ? "External Threat Actors" : "";
  const internalLabel = actors.some((a) => a.actor_type === "internal") ? "Internal Users"          : "";

  return vectors.map((av) => {
    const likelihood = normalizeSeverity(av.severity);
    const source =
      likelihood === "High"
        ? externalLabel || internalLabel || "Unknown"
        : internalLabel || externalLabel || "Unknown";
    return {
      name:       av.name,
      likelihood,
      source,
      stride:     av.stride_category ?? "",
      target:     av.targeted_asset  ?? "",
    };
  });
}

// ── Pie label renderers ───────────────────────────────────────────────────────

const renderLikelihoodLabel = ({ cx, cy, midAngle, outerRadius, name, value, percent, fill }: any) => {
  const R   = Math.PI / 180;
  const cos = Math.cos(-R * midAngle);
  const sin = Math.sin(-R * midAngle);
  const sx  = cx + (outerRadius + 8)  * cos;
  const sy  = cy + (outerRadius + 8)  * sin;
  const mx  = cx + (outerRadius + 28) * cos;
  const my  = cy + (outerRadius + 28) * sin;
  const ex  = mx + (cos >= 0 ? 1 : -1) * 18;
  const ey  = my;
  const anc = cos >= 0 ? "start" : "end";
  const tx  = ex + (cos >= 0 ? 5 : -5);
  return (
    <g>
      <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" strokeWidth={1} />
      <circle cx={ex} cy={ey} r={2} fill={fill} />
      <text x={tx} y={ey - 3}  textAnchor={anc} fill="#374151" fontSize={11} fontWeight="600">{name}</text>
      <text x={tx} y={ey + 11} textAnchor={anc} fill="#9ca3af"  fontSize={10}>{value} threats {Math.round(percent * 100)}%</text>
    </g>
  );
};

const renderSourceLabel = ({ cx, cy, midAngle, outerRadius, name, fill }: any) => {
  const R   = Math.PI / 180;
  const cos = Math.cos(-R * midAngle);
  const sin = Math.sin(-R * midAngle);
  const sx  = cx + (outerRadius + 8)  * cos;
  const sy  = cy + (outerRadius + 8)  * sin;
  const mx  = cx + (outerRadius + 28) * cos;
  const my  = cy + (outerRadius + 28) * sin;
  const ex  = mx + (cos >= 0 ? 1 : -1) * 18;
  const ey  = my;
  const anc = cos >= 0 ? "start" : "end";
  return (
    <g>
      <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" strokeWidth={1} />
      <circle cx={ex} cy={ey} r={2} fill={fill} />
      <text x={ex + (cos >= 0 ? 5 : -5)} y={ey + 4} textAnchor={anc} fill="#374151" fontSize={11}>{name}</text>
    </g>
  );
};

// ── STRIDE stacked bar chart ──────────────────────────────────────────────────

const STRIDE_BAR_H = 190;

const StrideBarChart: React.FC<{ data: { name: string; High: number; Med: number; Low: number }[] }> = ({ data }) => {
  const maxY = Math.max(...data.map((d) => d.High + d.Med + d.Low), 5);
  const ticks = Array.from({ length: maxY + 1 }, (_, i) => i).reverse();

  return (
    <div className="w-full flex flex-col" style={{ height: STRIDE_BAR_H + 72 }}>
      <div className="flex gap-1" style={{ height: STRIDE_BAR_H }}>
        {/* Y-axis ticks */}
        <div className="w-6 flex-shrink-0 relative">
          {ticks.map((n) => (
            <span
              key={n}
              className="absolute right-0 text-xs text-gray-500 leading-none"
              style={{ top: `${((maxY - n) / maxY) * STRIDE_BAR_H - 7}px` }}
            >
              {n}
            </span>
          ))}
        </div>

        {/* Stacked bars */}
        <div className="flex-1 flex items-end justify-around">
          {data.map((row) => {
            const total = row.High + row.Med + row.Low;
            const barH  = total > 0 ? (total / maxY) * STRIDE_BAR_H : 0;
            return (
              <div key={row.name} style={{ width: 48 }}>
                <div
                  className="w-full flex flex-col rounded-t-md overflow-hidden"
                  style={{ height: `${barH}px` }}
                >
                  {row.High > 0 && <div style={{ flex: row.High, background: C.red  }} />}
                  {row.Med  > 0 && <div style={{ flex: row.Med,  background: C.blue }} />}
                  {row.Low  > 0 && <div style={{ flex: row.Low,  background: C.green }} />}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* X-axis labels */}
      <div className="flex justify-around pl-7 pt-3">
        {data.map((row) => (
          <span key={row.name} className="text-xs font-semibold text-gray-600 text-center">
            {row.name}
          </span>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-3 pl-7">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm" style={{ background: C.red }} />
          <span className="text-xs text-gray-500">High</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm" style={{ background: C.blue }} />
          <span className="text-xs text-gray-500">Med</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm" style={{ background: C.green }} />
          <span className="text-xs text-gray-500">Low</span>
        </div>
      </div>
    </div>
  );
};

// ── Target Asset — custom div bars ────────────────────────────────────────────

const TargetAssetChart: React.FC<{ data: { name: string; High: number; Med: number; Low: number }[] }> = ({ data }) => {
  const maxUnit = Math.max(...data.map((d) => d.High + d.Med + d.Low), 1);
  return (
    <div className="space-y-3 mt-1">
      {data.map((row) => (
        <div key={row.name}>
          <div className="text-xs text-gray-500 mb-1">{row.name}</div>
          <div className="flex gap-1 items-center">
            {row.High > 0 && (
              <div
                className="h-3.5 rounded-sm flex-shrink-0"
                style={{ width: `${(row.High / maxUnit) * 75}%`, background: C.red }}
              />
            )}
            {row.Med > 0 && (
              <div
                className="h-3.5 rounded-sm flex-shrink-0"
                style={{ width: `${(row.Med / maxUnit) * 75}%`, background: C.blue }}
              />
            )}
            {row.Low > 0 && (
              <div
                className="h-3.5 rounded-sm flex-shrink-0"
                style={{ width: `${(row.Low / maxUnit) * 75}%`, background: C.green }}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Likelihood badge ──────────────────────────────────────────────────────────

const LikelihoodBadge: React.FC<{ value: Likelihood }> = ({ value }) => {
  const cls =
    value === "High" ? "bg-red-500"  :
    value === "Med"  ? "bg-blue-500" : "bg-green-500";
  return (
    <span className={`inline-block px-3 py-0.5 rounded-full text-white text-xs font-semibold ${cls}`}>
      {value}
    </span>
  );
};

// ── Dashboard ─────────────────────────────────────────────────────────────────

export const ThreatModelDashboard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = location.state as DashboardRouteState | null;

  const [state, dispatch] = useReducer(dashboardReducer, dashboardInitialState);
  const { selectedThreat } = state;

  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportDone, setExportDone] = useState(false);

  const handleExport = async () => {
    if (!routeState?.fileKey) return;
    setIsExporting(true);
    setExportError(null);
    setExportDone(false);
    try {
      await generateAllReport(routeState.fileKey, "DEV_API_KEY_TMA");
      setExportDone(true);
    } catch (err: any) {
      setExportError(err.message ?? "Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  const threatSources = (routeState as any)?.threatSources as ThreatSourcesResponse | null | undefined;

  const threats = threatSources ? mapThreatSourcesToThreats(threatSources) : [];
  const loading = false;
  const error   = !threatSources ? "No threat source data available. Please re-run the analysis." : null;

  const { likelihoodData, strideData, targetData, sourcesData } = deriveChartData(threats);

  const title       = routeState?.title       ?? "Threat Model";
  const description = routeState?.description ?? "";

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h2 className="text-blue-600 font-bold text-2xl">{title}</h2>
            {description && (
              <p className="text-gray-500 text-sm mt-1 max-w-xl leading-relaxed">{description}</p>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/console/threat-accelerator")}
                className="inline-flex items-center gap-1.5 text-sm text-purple-600 border border-purple-200 bg-purple-50 hover:bg-purple-100 px-5 py-2.5 rounded-full font-semibold shadow-sm transition whitespace-nowrap"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7m-9 5v6h4v-6m-4 0H6a2 2 0 01-2-2V9" />
                </svg>
                Home
              </button>
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="bg-gradient-to-r from-teal-500 to-green-500 text-white text-sm px-6 py-2.5 rounded-full font-semibold shadow hover:shadow-md transition disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
              >
              {isExporting ? (
                <>
                  <div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download Report
                </>
              )}
              </button>
            </div>

            {exportDone && (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                ZIP downloaded (PDF · JSON · DOCX)
              </span>
            )}
            {exportError && (
              <p className="text-red-500 text-xs max-w-xs text-right">{exportError}</p>
            )}
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-24 text-gray-400 text-sm">
            <div className="w-5 h-5 rounded-full border-2 border-purple-300 border-t-purple-600 animate-spin mr-3" />
            Loading threat model report…
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-600 text-sm text-center">
            Failed to load report: {error}
          </div>
        )}

        {/* Dashboard content */}
        {!loading && !error && (
          <>
            {/* Charts grid */}
            <div className="grid grid-cols-2 gap-6 mb-6">

              {/* Threats by Likelihood */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h4 className="text-blue-600 font-semibold mb-2">Threats by Likelihood</h4>
                <div className="h-72 [&_svg]:overflow-visible">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart margin={{ top: 30, right: 90, bottom: 30, left: 30 }}>
                      <Pie
                        data={likelihoodData}
                        dataKey="value"
                        nameKey="name"
                        cx="40%"
                        cy="50%"
                        outerRadius={75}
                        label={renderLikelihoodLabel}
                        labelLine={false}
                        isAnimationActive={false}
                      >
                        {likelihoodData.map((_, i) => (
                          <Cell key={i} fill={LIKELIHOOD_COLORS[i % LIKELIHOOD_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Threats by STRIDE Category */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h4 className="text-blue-600 font-semibold mb-4">Threats by STRIDE Category</h4>
                {(() => {
                  const filtered = strideData.filter((d) =>
                    ["Spoofing", "Repudiation", "Denial of Service"].includes(d.name)
                  );
                  return filtered.length > 0
                    ? <StrideBarChart data={filtered} />
                    : <p className="text-gray-400 text-sm italic">No data available</p>;
                })()}
              </div>

              {/* Threats by Target Asset */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h4 className="text-blue-600 font-semibold mb-4">Threats by Target Asset</h4>
                {targetData.length > 0
                  ? <TargetAssetChart data={targetData} />
                  : <p className="text-gray-400 text-sm italic">No data available</p>
                }
              </div>

              {/* Threat Sources */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h4 className="text-blue-600 font-semibold mb-2">Threat Sources</h4>
                <div className="h-80 [&_svg]:overflow-visible">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart margin={{ top: 30, right: 110, bottom: 30, left: 80 }}>
                      <Pie
                        data={sourcesData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={68}
                        outerRadius={100}
                        label={renderSourceLabel}
                        labelLine={false}
                        isAnimationActive={false}
                      >
                        {sourcesData.map((_, i) => (
                          <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* All Threats Table */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-blue-600 font-semibold text-lg">All Threats</h4>
                <span className="text-purple-600 text-sm font-semibold">{threats.length} Threats</span>
              </div>
              {threats.length === 0 ? (
                <p className="text-gray-400 text-sm italic text-center py-6">No threats found</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-700 border-b border-gray-200">
                      <th className="pb-3 font-semibold">Threat name</th>
                      <th className="pb-3 font-semibold">Likelihood</th>
                      <th className="pb-3 font-semibold">Source</th>
                      <th className="pb-3 font-semibold">STRIDE Category</th>
                      <th className="pb-3 font-semibold">Target Asset</th>
                    </tr>
                  </thead>
                  <tbody>
                    {threats.map((t, idx) => (
                      <tr
                        key={idx}
                        className="border-b border-gray-100 hover:bg-gray-50 transition cursor-pointer"
                       /*  onClick={() => dispatch({ type: "SELECT_THREAT", payload: t })} */
                      >
                        <td className="py-3 pr-4 font-semibold text-gray-900 text-xs">{t.name}</td>
                        <td className="py-3 pr-4"><LikelihoodBadge value={t.likelihood} /></td>
                        <td className="py-3 pr-4 text-gray-600 text-xs">{t.source}</td>
                        <td className="py-3 pr-4 text-gray-600 text-xs">{t.stride}</td>
                        <td className="py-3 text-gray-600 text-xs">{t.target}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
             {/*  <ThreatDetailModal
                open={!!selectedThreat}
                onClose={() => dispatch({ type: "SELECT_THREAT", payload: null })}
                threat={selectedThreat ? { ...selectedThreat, title: selectedThreat.name } : undefined}
              /> */}
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default ThreatModelDashboard;
